import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Briefcase, Package, Search, Target } from 'lucide-react'
import { KaryvaLogoAnimated3D } from '../components/KaryvaLogoAnimated3D'

type ModuleCard = {
  key: 'strategy' | 'product' | 'program'
  title: string
  description: string
  features: string[]
  icon: typeof Target
  to: string
  cta: string
  iconGradient: string
  borderTop: string
  buttonGradient: string
  glow: string
}

const ALL_MODULES: ModuleCard[] = [
  {
    key: 'strategy',
    title: 'Strategy',
    description: 'Company objectives, measurable key results, and the work that delivers them',
    features: ['Objectives & Key Results', 'Progress vs. time', 'Strategic alignment', 'Unaligned work'],
    icon: Target,
    to: '/strategy',
    cta: 'Enter Strategy',
    iconGradient: 'from-blue-500 to-indigo-600',
    borderTop: 'from-blue-500 to-indigo-500',
    buttonGradient: 'from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700',
    glow: 'shadow-blue-500/20',
  },
  {
    key: 'product',
    title: 'Product',
    description: 'Products and their lifecycle, the work that builds them, and how they perform',
    features: ['Products & lifecycle', 'Roadmap from real project dates', 'Ideas backlog (RICE)', 'Product metrics'],
    icon: Package,
    to: '/product',
    cta: 'Enter Product',
    iconGradient: 'from-fuchsia-500 to-purple-600',
    borderTop: 'from-fuchsia-500 to-purple-500',
    buttonGradient: 'from-fuchsia-500 to-purple-600 hover:from-fuchsia-600 hover:to-purple-700',
    glow: 'shadow-fuchsia-500/20',
  },
  {
    key: 'program',
    title: 'Program & Projects',
    description: 'End-to-end program and project management with governance and compliance',
    features: ['Program Management', 'Project Tracking', 'Governance & Compliance', 'RAID Logs'],
    icon: Briefcase,
    to: '/dashboard',
    cta: 'Enter Program & Projects',
    iconGradient: 'from-emerald-500 to-teal-600',
    borderTop: 'from-emerald-500 to-teal-500',
    buttonGradient: 'from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700',
    glow: 'shadow-emerald-500/20',
  },
]

// Strategy became a real page on 2026-09-29, Product on 2026-10-01.
const MODULES: ModuleCard[] = ALL_MODULES

const SEARCH_INDEX: Array<{ label: string; to: string; module: string }> = [
  { label: 'Executive Dashboard', to: '/dashboard', module: 'Strategy' },
  { label: 'Portfolio Management', to: '/portfolio', module: 'Strategy' },
  { label: 'Strategic Insights', to: '/ai', module: 'Strategy' },
  { label: 'Portfolio Analytics', to: '/portfolio/analytics', module: 'Strategy' },
  { label: 'Products', to: '/product', module: 'Product' },
  { label: 'Product Roadmap', to: '/product?tab=roadmap', module: 'Product' },
  { label: 'Ideas Backlog', to: '/product?tab=ideas', module: 'Product' },
  { label: 'Program Management', to: '/program', module: 'Program' },
  { label: 'Project Tracking', to: '/project', module: 'Program' },
  { label: 'Governance & Compliance', to: '/portfolio/governance', module: 'Program' },
  { label: 'RAID Logs', to: '/risks', module: 'Program' },
  { label: 'Lessons Learned', to: '/lessons', module: 'Program' },
  { label: 'Tasks', to: '/task', module: 'Program' },
  { label: 'Teams', to: '/teams', module: 'Program' },
  { label: 'Departments', to: '/departments', module: 'Program' },
  { label: 'AI Assistant', to: '/ai', module: 'Strategy' },
  { label: 'My Day', to: '/home', module: 'Program' },
]

export default function PMOLanding() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    return SEARCH_INDEX.filter((item) => item.label.toLowerCase().includes(q)).slice(0, 8)
  }, [query])

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0a0f1c] text-white">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-[420px] w-[420px] rounded-full bg-fuchsia-600/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-emerald-600/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 py-16 lg:py-20">
        <div className="flex flex-col items-center text-center">
          <div className="mb-10 rounded-3xl border border-white/10 bg-white/5 p-8 shadow-2xl shadow-black/40 backdrop-blur">
            <div className="rounded-2xl bg-[#f4f1ea] px-10 py-8">
              <KaryvaLogoAnimated3D size="large" darkBg={false} autoPlay />
            </div>
          </div>

          <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">
            Project Management Office
          </h1>
          <p className="mt-4 max-w-2xl text-base text-white/70 sm:text-lg">
            Enterprise Portfolio, Product &amp; Program Management Platform
          </p>

          <div className="relative mt-10 w-full max-w-2xl">
            <div className="flex items-center rounded-2xl border border-white/10 bg-white/5 px-5 py-4 backdrop-blur transition focus-within:border-white/30 focus-within:bg-white/10">
              <Search className="mr-3 h-5 w-5 text-white/50" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search modules and features..."
                className="w-full bg-transparent text-base text-white placeholder:text-white/40 focus:outline-none"
                aria-label="Search modules and features"
              />
            </div>
            {matches.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-2xl border border-white/10 bg-[#0f1626]/95 shadow-2xl backdrop-blur">
                {matches.map((m) => (
                  <button
                    key={`${m.label}-${m.to}`}
                    type="button"
                    onClick={() => navigate(m.to)}
                    className="flex w-full items-center justify-between px-5 py-3 text-left text-sm text-white/80 transition hover:bg-white/5"
                  >
                    <span>{m.label}</span>
                    <span className="text-xs uppercase tracking-wider text-white/40">{m.module}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={`mt-16 grid gap-6 ${MODULES.length === 1 ? 'mx-auto w-full max-w-md' : 'lg:grid-cols-3'}`}>
          {MODULES.map((m) => {
            const Icon = m.icon
            return (
              <div
                key={m.key}
                className={`group relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur transition hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.07] ${m.glow} hover:shadow-2xl`}
              >
                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${m.borderTop}`} />

                <div
                  className={`mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${m.iconGradient} shadow-lg`}
                >
                  <Icon className="h-7 w-7 text-white" strokeWidth={2.25} />
                </div>

                <h2 className="text-2xl font-semibold text-white">{m.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-white/65">{m.description}</p>

                <ul className="mt-6 space-y-2.5">
                  {m.features.map((f) => (
                    <li key={f} className="flex items-center text-sm text-white/75">
                      <span className={`mr-3 h-1.5 w-1.5 rounded-full bg-gradient-to-r ${m.borderTop}`} />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => navigate(m.to)}
                  className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r ${m.buttonGradient} px-5 py-3 text-sm font-medium text-white shadow-lg transition`}
                >
                  {m.cta}
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
